import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import useUnauthorizedHandler from "../snippet/useUnauthorizedHandler";
import { debounce } from "../snippet/Debouncing";
import { GetAllServiceTypes } from "../../actions/Admin";

function ServicesMultiSelectDropdown({ title, updateFilter }) {
  const [selectedServices, setSelectedServices] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [items, setItems] = useState([]);
  const dropdownRef = useRef(null);
  const handleUnauthorizedAccess = useUnauthorizedHandler();
  const dispatch = useDispatch();

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const inputchangeSearch = useCallback(debounce(handleSearchChange), []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (item) => {
    const serviceName = item.service_name;
    const exists = selectedServices.includes(serviceName);
    if (exists) {
      setSelectedServices(prevState => prevState.filter(entry => entry !== serviceName));
    } else {
      setSelectedServices(prevState => [...prevState, serviceName]);
    }
  };

  const clearSelection = () => {
    setSelectedServices([]);
  };

  useEffect(() => {
    updateFilter("services", selectedServices);
  }, [selectedServices]);

  const fetchServices = useCallback(async () => {
    try {
      const response = await dispatch(GetAllServiceTypes(undefined, searchTerm));
      if (response?.success === true && response?.data) {
        const rows = Array.isArray(response.data.rows) ? response.data.rows : [];
        const mapped = rows.map(item => ({
          service_type_id: item.master_setting_id,
          service_name: item.meta_value_one
        }));
        setItems(mapped);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  }, [dispatch, searchTerm]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  return (
    <div className="relative inline-block text-left w-40" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        type="button"
        className="flex items-center justify-between w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
      >
        <span className="truncate">{title}</span>
        {selectedServices?.length > 0 && (
          <span className="inline-flex ml-1 items-center justify-center w-6 h-6 text-sm font-semibold text-blue-700 bg-blue-100 rounded-full">
            {selectedServices.length}
          </span>
        )}
        <svg
          className="w-5 h-5 ml-2 text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-10">
          <div className="flex items-center justify-between p-2 border-b border-gray-200">
            <span className="text-sm text-gray-500">
              {selectedServices.length} selected
            </span>
            <button
              type="button"
              className="text-sm text-blue-600 hover:underline focus:outline-none"
              onClick={clearSelection}
            >
              Clear
            </button>
          </div>

          <div className="p-2">
            <input
              type="text"
              placeholder="Search services"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              onChange={(e) => inputchangeSearch(e)}
            />
          </div>

          <ul className="max-h-48 overflow-y-auto">
            {items?.map((item) => (
              <li
                key={item.service_type_id}
                className="flex items-center px-3 py-2 hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  className="w-4 h-4 mr-2 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  checked={selectedServices.includes(item.service_name)}
                  onChange={() => handleSelect(item)}
                />
                <span className="text-sm text-gray-700 font-normal">
                  {item.service_name}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default ServicesMultiSelectDropdown;
